import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";
import { getEnrollmentProgress, getEnrollmentValidationErrors, initial, mapEnrollmentPayloadToFormState, mapFormStateToEnrollment, mapStudentProfileToFormState } from "../pages/student/onlineEnrolmentUtils";
import { canEditEnrollment, EnrollmentStatus } from "@/utils/enrolmentStatus";
import { STUDENT_DOCUMENT_DEFINITIONS } from "@/constants/studentDocuments";

const getApiBaseUrl = () => (process.env.REACT_APP_API_URL).replace(/\/$/, "");

async function fetchEnrollmentPayload(studentBookingId) {
  if (!studentBookingId) return null;

  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentEnrollment/Enrollment`, {
      params: { pStudentBookingId: studentBookingId },
    });

    const data = response?.data;
    if (data) {
      if (data.enrollment || data.studentEnrollment) {
        const enrollment = { ...(data.enrollment || data.studentEnrollment) };

        if (enrollment.speakOtherLanguage !== undefined && enrollment.speekOtherLanguage === undefined) {
          enrollment.speekOtherLanguage = enrollment.speakOtherLanguage;
        }

        return {
          isSuccess: data.isSuccess ?? true,
          message: data.message ?? "",
          studentEnrollment: enrollment,
          masterData: data.masterData ?? {},
          booking: data.booking ?? {},
          documents: data.documents ?? data.studentDocuments ?? enrollment.documents ?? enrollment.studentDocuments ?? [],
        };
      }

      return data;
    }
  } catch (error) {
    console.error("Failed to load enrollment payload", error);
    ToastHelper.error("Unable to load enrolment data right now.");
  }

  if (String(studentBookingId) === "1001") {
    return null;
  }

  return null;
}

async function fetchMasterData() {
  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentEnrollment/MasterData`);
    const data = response?.data;
    if (data) {
      if (data.masterData) return data.masterData;
      return data;
    }
    return {};
  } catch (err) {
    console.error("Failed to load masterData", err);
    return {};
  }
}

async function fetchStudentProfile() {
  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentProfile`);
    return response?.data || null;
  } catch (error) {
    console.error("Failed to load student profile for enrolment", error);
    return null;
  }
}

const mergeMissingValues = (current, defaults) => Object.keys(current).reduce((result, key) => {
  const value = current[key];
  const isMissing = value === "" || value === null || value === undefined || value === 0;
  result[key] = isMissing && defaults[key] !== undefined ? defaults[key] : value;
  return result;
}, {});


const createDefaultDocuments = () => STUDENT_DOCUMENT_DEFINITIONS.map((definition) => ({
  studentDocTypeId: definition.id,
  key: definition.key,
  label: definition.name,
  file: null,
  fileName: "",
  studentDocId: null,
}));

const mergeEnrollmentDocuments = (sourceDocuments = []) => {
  const existingDocuments = Array.isArray(sourceDocuments) ? sourceDocuments : [];

  return createDefaultDocuments().map((defaultDocument) => {
    const existing = existingDocuments.find((document) =>
      Number(
        document?.studentDocTypeId
        ?? document?.documentTypeId
        ?? document?.StudentDocTypeId
        ?? document?.DocumentTypeId
      ) === defaultDocument.studentDocTypeId
    );

    if (!existing) return defaultDocument;

    return {
      ...defaultDocument,
      studentDocId: existing.studentDocId
        ?? existing.documentId
        ?? existing.studentEnrollmentDocumentId
        ?? existing.StudentDocId
        ?? null,
      fileName: existing.fileName ?? existing.documentName ?? existing.FileName ?? "",
      fileType: existing.fileType ?? existing.contentType ?? existing.FileType ?? "",
      fileSize: existing.fileSize ?? existing.FileSize ?? 0,
      downloadUrl: existing.downloadUrl ?? existing.fileUrl ?? existing.DownloadUrl ?? "",
      issueDate: existing.issueDate ?? existing.IssueDate ?? null,
      expiryDate: existing.expiryDate ?? existing.ExpiryDate ?? null,
      remarks: existing.remarks ?? existing.Remarks ?? "",
      documentDescription: existing.documentDescription ?? existing.DocumentDescription ?? "",
      file: null,
      isPendingUpload: false,
    };
  });
};



const useOnlineEnrollment = (studentBookingId) => {
  const navigate = useNavigate();
  const [d, setD] = useState(initial);
  const [active, setActive] = useState("usi");
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [masterData, setMasterData] = useState({});
  const [masterLoading, setMasterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [studentEnrollment, setStudentEnrollment] = useState(null);
  const [enrolmentStatus, setEnrolmentStatus] = useState(EnrollmentStatus.Draft);
  const [documents, setDocuments] = useState(createDefaultDocuments);

  useEffect(() => {
    let isMounted = true;

    const loadEnrollment = async () => {
      if (!studentBookingId) {
        setLoadError(false);
        setD(initial);
        setStudentEnrollment(null);
        setDocuments(createDefaultDocuments());
        return;
      }

      setLoading(true);
      setLoadError(false);

      const [enrollmentPayload, profile] = await Promise.all([
        fetchEnrollmentPayload(studentBookingId),
        fetchStudentProfile(),
      ]);
      if (!isMounted) return;

      if (!enrollmentPayload) {
        setLoading(false);
        setLoadError(true);
        return;
      }

      let payload = enrollmentPayload;
      if (!payload.masterData || Object.keys(payload.masterData).length === 0) {
        setMasterLoading(true);
        const loadedMasterData = await fetchMasterData();
        if (!isMounted) return;
        payload = { ...payload, masterData: loadedMasterData || {} };
        setMasterLoading(false);
      }

      const enrollmentData = payload.studentEnrollment || payload.enrollment || payload;
      const loadedDocuments =
        payload.documents
        || payload.studentDocuments
        || enrollmentData.documents
        || enrollmentData.studentDocuments
        || enrollmentData.vo_DocList
        || enrollmentData.VO_DocList
        || [];
      setDocuments(mergeEnrollmentDocuments(loadedDocuments));
      const loadedStatus = Number(enrollmentData.enrolmentStatus || payload.enrolmentStatus || EnrollmentStatus.Draft);
      setEnrolmentStatus(loadedStatus);
      const profileDefaults = mapStudentProfileToFormState(profile, payload.masterData || {});
      setStudentEnrollment(enrollmentData);
      const hasUsableData = Boolean(
        enrollmentData &&
        (
          enrollmentData.studentEnrollmentId ||
          enrollmentData.enrollmentId ||
          enrollmentData.usiNumber ||
          enrollmentData.firstName ||
          enrollmentData.firstName ||
          enrollmentData.email ||
          enrollmentData.mobileNumber ||
          enrollmentData.currentStep ||
          enrollmentData.currentStepNo
        )
      );

      if (!hasUsableData) {
        if (payload.masterData) {
          setMasterData(payload.masterData);
        } else {
          setMasterLoading(true);
          fetchMasterData().then((md) => { if (isMounted) setMasterData(md || {}); }).finally(() => { if (isMounted) setMasterLoading(false); });
        }

        setD({ ...initial, ...profileDefaults });
        setActive("usi");
        setSubmission(null);
        setLoading(false);
        setLoadError(false);
        return;
      }

      const nextState = mapEnrollmentPayloadToFormState(payload);
      const completedState = mergeMissingValues(nextState, profileDefaults);
      setD(completedState);
      setActive(nextState.activeSection || "usi");
      const showReadOnlySummary = !canEditEnrollment(loadedStatus) && Boolean(enrollmentData.studentEnrollmentId);
      setSubmission(showReadOnlySummary ? {
        ref: enrollmentData.enrolmentNumber,
        submittedAt: enrollmentData.submittedDate || new Date().toISOString(),
        data: completedState,
        enrolmentStatus: loadedStatus,
      } : null);
      if (payload.masterData) {
        setMasterData(payload.masterData);
      } else {
        setMasterLoading(true);
        fetchMasterData().then((md) => { if (isMounted) setMasterData(md || {}); }).finally(() => { if (isMounted) setMasterLoading(false); });
      }
      setLoading(false);
      ToastHelper.success("Existing enrolment details loaded");
    };

    loadEnrollment();

    return () => { isMounted = false; };
  }, [studentBookingId]);

  const set = (k, v) => setD((current) => ({ ...current, [k]: v }));

  const requiredCount = useMemo(() => {
    return getEnrollmentProgress(d, documents);
  }, [d, documents]);

  const canEdit = canEditEnrollment(enrolmentStatus);
  const canSubmit = canEdit && Boolean(studentBookingId || d.studentBookingId);

  const save = async ({ submit = false } = {}) => {
    try {
      setSaving(true);
      const apiBaseUrl = getApiBaseUrl();
      if (!studentBookingId && !d.studentBookingId) {
        ToastHelper.error("Please open enrolment from a course booking before saving.");
        return { success: false };
      }
      if (!canEdit) {
        ToastHelper.error("This enrolment is read-only in its current status.");
        return { success: false };
      }
      const oldItem = { ...(studentEnrollment || {}) };
      const mappedFormState = mapFormStateToEnrollment(d, documents);
      const enrollment = {
        ...(studentEnrollment || {}),
        ...mappedFormState,
      };

      enrollment.studentBookingId = Number(studentBookingId || d.studentBookingId);

      if (submit) {
        enrollment.isSubmitted = true;
        enrollment.submittedDate = new Date().toISOString();
        enrollment.declarationAccepted = true;
      }

      const request = {
        Message: "",
        Submit: submit,
        Enrollment: enrollment,
        OldVO: oldItem,
      };

      const multipartData = new FormData();
      multipartData.append("EnrollmentJson", JSON.stringify(request));

      documents.forEach((document, index) => {
        multipartData.append(
          `Documents[${index}].StudentDocTypeId`,
          String(document.studentDocTypeId)
        );

        if (document.studentDocId) {
          multipartData.append(
            `Documents[${index}].StudentDocId`,
            String(document.studentDocId)
          );
        }

        if (document.issueDate) multipartData.append(`Documents[${index}].IssueDate`, document.issueDate);
        if (document.expiryDate) multipartData.append(`Documents[${index}].ExpiryDate`, document.expiryDate);
        if (document.remarks) multipartData.append(`Documents[${index}].Remarks`, document.remarks);
        if (document.documentDescription) {
          multipartData.append(
            `Documents[${index}].DocumentDescription`,
            document.documentDescription
          );
        }

        if (typeof File !== "undefined" && document.file instanceof File) {
          multipartData.append(
            `Documents[${index}].File`,
            document.file,
            document.file.name
          );
        }
      });

      const response = await apiClient.post(
        `${apiBaseUrl}/StudentEnrollment/Save`,
        multipartData
      );

      if (response?.data) {
        const returned = response.data;
        const normalized = returned.studentEnrollment || returned.enrollment ? returned : null;

        if (returned.studentEnrollmentId) {
          const savedEnrollmentId = Number(returned.studentEnrollmentId);
          setStudentEnrollment((current) => ({ ...current, studentEnrollmentId: savedEnrollmentId }));
          setD((current) => ({ ...current, studentEnrollmentId: String(savedEnrollmentId) }));
        }
        if (returned.enrolmentStatus) setEnrolmentStatus(Number(returned.enrolmentStatus));

        const returnedDocuments =
          returned.documents
          || returned.studentDocuments
          || normalized?.documents
          || normalized?.studentDocuments;

        if (Array.isArray(returnedDocuments)) {
          setDocuments(mergeEnrollmentDocuments(returnedDocuments));
        } else {
          setDocuments((current) => current.map((document) => ({
            ...document,
            file: null,
            isPendingUpload: false,
          })));
        }

        if (normalized?.masterData) setMasterData(normalized.masterData);
        if (normalized?.studentEnrollment || normalized?.enrollment) {
          const nextState = mapEnrollmentPayloadToFormState(normalized);
          setD(nextState);
          setActive(nextState.activeSection || "usi");
          setLoadError(false);
        }

        ToastHelper.success(submit ? "Enrolment submitted successfully." : "Progress saved — you can resume this enrolment any time");
        return { success: true, payload: normalized || returned };
      }

      ToastHelper.success(submit ? "Enrolment submitted successfully." : "Progress saved — you can resume this enrolment any time");
      return { success: true };
    } catch (err) {
      console.error("Failed to save enrolment", err);
      const validationErrors = err.response?.data?.errors
        || err.response?.data?.validationResult?.errors;
      const validationMessages = Array.isArray(validationErrors)
        ? validationErrors.map((error) => error?.message || String(error)).filter(Boolean)
        : validationErrors && typeof validationErrors === "object"
          ? Object.entries(validationErrors).flatMap(([field, messages]) =>
            (Array.isArray(messages) ? messages : [messages]).map((message) => `${field}: ${message}`))
          : [];
      const apiMessage = err.response?.data?.message
        || err.response?.data?.title
        || (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(", ") : "");
      if (validationMessages.length > 0) {
        ToastHelper.error(validationMessages.join("\n"), {
          className: "whitespace-pre-line",
        });
      } else {
        ToastHelper.error(apiMessage || (submit ? "Unable to submit enrolment right now." : "Unable to save progress right now."));
      }
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const saveAndReturn = async () => {
    const result = await save();
    if (result.success) {
      navigate("/student/my-courses");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { ToastHelper.error("Open enrolment from a booking before submitting."); return; }
    const validationErrors = getEnrollmentValidationErrors(d);
    if (validationErrors.length > 0) {
      ToastHelper.error(validationErrors.join("\n"), {
        className: "whitespace-pre-line",
      });
      return;
    }

    const result = await save({ submit: true });
    if (!result.success) return;

    const ref = result.payload?.enrolmentNumber;
    if (!ref) {
      ToastHelper.error("The server did not return an enrolment reference.");
      return;
    }
    setSubmission({
      ref,
      submittedAt: new Date().toISOString(),
      data: {
        ...d,
        studentEnrollmentId: result.payload?.studentEnrollmentId ?? d.studentEnrollmentId,
        studentBookingId: studentBookingId ?? d.studentBookingId,
      enrolmentNumber: ref,
      },
      enrolmentStatus: EnrollmentStatus.Submitted,
    });
    setEnrolmentStatus(EnrollmentStatus.Submitted);
    ToastHelper.success(`Enrolment ${ref} submitted successfully.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startNew = () => {
    setSubmission(null);
    setStudentEnrollment(null);
    setD(initial);
    setDocuments(createDefaultDocuments());
    setActive("usi");
  };
  const cancel = () => {
    if (confirm("Discard all changes?")) {
      setD(initial);
      setDocuments(createDefaultDocuments());
      setActive("usi");
      ToastHelper.info("Form cleared");
    }
  };
  const jumpTo = (id) => { setActive(id); document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return {
    d,
    set,
    active,
    setActive,
    submission,
    loading,
    loadError,
    masterData,
    masterLoading,
    saving,
    requiredCount,
    canSubmit,
    canEdit,
    enrolmentStatus,
    documents,
    setDocuments,
    save,
    saveAndReturn,
    submit,
    startNew,
    cancel,
    jumpTo,
  };
};

export default useOnlineEnrollment;
